import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CalendarMonthOutlined,
  FilterListOutlined,
  GroupsOutlined,
  PictureAsPdfOutlined,
  RefreshOutlined,
  SaveOutlined,
  SchoolOutlined,
  SubjectOutlined,
} from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import { toast } from 'react-toastify';
import { ClassReportCardsDocument } from '../../components/pdf/StudentReportCardPDF';
import api from '../../utils/Api';
import {
  getApiErrorMessage,
  getClassDisplayName,
  getSchoolId,
  getStoredTeacher,
  teacherPortalGridItemSx,
  teacherPortalStatCardSx,
  teacherPortalTableCardSx,
} from './teacherPortalUtils';

const subjectFromValue = (subject) => {
  if (!subject) return null;
  if (typeof subject === 'string') return { _id: subject, name: subject };
  return {
    _id: subject._id || subject.id || subject.name,
    name: subject.name || subject.subjectName || subject.shortName || 'Subject',
  };
};

const normalizeSubjects = (teacher, selectedClass) => {
  const fromClass = Array.isArray(selectedClass?.subjects)
    ? selectedClass.subjects.map(subjectFromValue).filter(Boolean)
    : [];
  const fromTeacher = [
    ...(Array.isArray(teacher?.teacher?.subjects) ? teacher.teacher.subjects : []),
    ...(Array.isArray(teacher?.subjects) ? teacher.subjects : []),
    ...(Array.isArray(teacher?.assignedSubjects) ? teacher.assignedSubjects : []),
  ]
    .map(subjectFromValue)
    .filter(Boolean);

  const merged = [...fromClass, ...fromTeacher];
  return merged.filter(
    (subject, index, self) =>
      subject?._id && index === self.findIndex((item) => String(item._id) === String(subject._id))
  );
};

const CONDUCT_OPTIONS = [
  { label: 'Good social skills', value: 'Good social skills' },
  { label: 'Always attentive in class', value: 'Always attentive in class' },
  { label: 'Exhibits good teamwork', value: 'Exhibits good teamwork' },
  { label: 'Always in class on time', value: 'Always in class on time' },
  { label: 'Asks question in class', value: 'Asks question in class' },
  { label: 'Dull in class', value: 'Dull in class' },
  { label: 'Makes noise in class', value: 'Makes noise in class' },
  {
    label: 'Enjoys conversation with friends during free periods',
    value: 'Enjoys conversation with friends during free periods',
  },
  {
    label: 'Remains an active learner throughout the school day',
    value: 'Remains an active learner throughout the school day',
  },
  { label: 'Reads extensively for enjoyment', value: 'Reads extensively for enjoyment' },
  { label: 'Welcomes leadership roles in groups', value: 'Welcomes leadership roles in groups' },
  { label: 'Shows enthusiasm for classroom activities', value: 'Shows enthusiasm for classroom activities' },
  {
    label: 'Exhibits a positive outlook and attitude in the classroom',
    value: 'Exhibits a positive outlook and attitude in the classroom',
  },
  { label: 'Responds appropriately when corrected', value: 'Responds appropriately when corrected' },
  { label: 'Is a model citizen in our classroom', value: 'Is a model citizen in our classroom' },
  { label: 'Asks for clarification when needed', value: 'Asks for clarification when needed' },
  { label: 'Completes assignments in the time allotted', value: 'Completes assignments in the time allotted' },
  { label: 'Displays self-discipline', value: 'Displays self-discipline' },
  {
    label: 'Uses free minutes of class time constructively',
    value: 'Uses free minutes of class time constructively',
  },
];

const INTEREST_OPTIONS = [
  { label: 'Singing songs', value: 'Singing songs' },
  { label: 'Reading books', value: 'Reading books' },
  { label: 'Dancing', value: 'Dancing' },
  { label: 'Poetry reciting', value: 'Poetry reciting' },
  { label: 'Playing Games', value: 'Playing Games' },
  { label: 'Sports activities', value: 'Sports activities' },
  { label: 'Acting drama', value: 'Acting drama' },
  { label: 'Playing drums', value: 'Playing drums' },
  { label: 'Drawing', value: 'Drawing' },
  { label: 'Writing', value: 'Writing' },
  { label: 'Bike riding', value: 'Bike riding' },
  { label: 'Swimming', value: 'Swimming' },
  { label: 'Cooking food', value: 'Cooking food' },
  { label: 'Operating computer', value: 'Operating computer' },
  { label: 'Composing songs', value: 'Composing songs' },
  { label: 'Rapping', value: 'Rapping' },
  { label: 'Spelling of words', value: 'Spelling of words' },
];

const REMARKS_OPTIONS = [
  { label: 'Well done, keep it up', value: 'Well done, keep it up' },
  { label: 'Poor performance, work hard', value: 'Poor performance, work hard' },
  { label: 'Can do better', value: 'Can do better' },
  { label: 'More room for improvement', value: 'More room for improvement' },
  { label: 'Learn hard to improve', value: 'Learn hard to improve' },
  { label: 'Needs more time study at home', value: 'Needs more time study at home' },
  { label: 'Needs necessary learning materials', value: 'Needs necessary learning materials' },
  { label: 'Making steady progress', value: 'Making steady progress' },
  { label: 'Needs Counseling', value: 'Needs Counseling' },
  { label: 'Performance was good', value: 'Performance was good' },
  { label: 'Strive for excellence', value: 'Strive for excellence' },
  { label: 'Take your studies seriously', value: 'Take your studies seriously' },
  { label: 'Improve on your spelling', value: 'Improve on your spelling' },
  { label: 'Read through your work before submitting', value: 'Read through your work before submitting' },
];

const CLASS_PROMOTION_ORDER = [
  'CRECHE',
  'NURSERY 1',
  'NURSERY 2',
  'KG 1',
  'KG 2',
  'BASIC 1',
  'BASIC 2',
  'BASIC 3',
  'BASIC 4',
  'BASIC 5',
  'BASIC 6',
  'BASIC 7',
  'BASIC 8',
  'BASIC 9',
];

const GRADE_PROMOTION_ORDER = [
  'GRADE 1',
  'GRADE 2',
  'GRADE 3',
  'GRADE 4',
  'GRADE 5',
  'GRADE 6',
  'GRADE 7',
  'GRADE 8',
  'GRADE 9',
];

const REPEATED_PROMOTION_VALUE = 'REPEATED';

const normalizeClassLabel = (value = '') => String(value).toUpperCase().replace(/\s+/g, ' ').trim();

const getEntityId = (value) => {
  if (!value) return '';
  return typeof value === 'string' ? value : value._id || value.id || value.userId || '';
};

const formatDateForReport = (dateValue) => {
  if (!dateValue) return '';
  const date = dateValue instanceof Date ? dateValue : new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const parseReportDateForInput = (value) => {
  if (!value || value === 'TBD') return '';
  const ddmmyyyy = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, day, month, year] = ddmmyyyy;
    return `${year}-${month}-${day}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getClassBaseName = (cls) => {
  const stream = normalizeClassLabel(cls?.stream || '');
  const rawName = String(cls?.name || cls?.className || cls?.displayName || cls?.classDisplayName || '');
  let normalizedName = normalizeClassLabel(rawName);
  if (stream && normalizedName.endsWith(stream)) {
    normalizedName = normalizeClassLabel(normalizedName.slice(0, -stream.length));
  }
  return normalizedName;
};

const getClassStream = (cls) => normalizeClassLabel(cls?.stream || '');

const getNextClassBaseName = (cls) => {
  const currentBaseName = getClassBaseName(cls);
  const promotionOrder = currentBaseName.startsWith('GRADE ')
    ? GRADE_PROMOTION_ORDER
    : CLASS_PROMOTION_ORDER;
  const currentIndex = promotionOrder.indexOf(currentBaseName);
  if (currentIndex === -1 || currentIndex >= promotionOrder.length - 1) return '';
  return promotionOrder[currentIndex + 1];
};

const getNextClassDisplayName = (selectedClass, classes) => {
  const nextBaseName = getNextClassBaseName(selectedClass);
  if (!nextBaseName) return '';
  const selectedStream = getClassStream(selectedClass);
  const matchingNextClass =
    classes.find(
      (classObj) =>
        getClassBaseName(classObj) === nextBaseName &&
        getClassStream(classObj) === selectedStream
    ) ||
    classes.find(
      (classObj) =>
        getClassBaseName(classObj) === nextBaseName &&
        !getClassStream(classObj)
    ) ||
    classes.find((classObj) => getClassBaseName(classObj) === nextBaseName);
  if (matchingNextClass) return getClassDisplayName(matchingNextClass);
  return selectedStream ? `${nextBaseName}${selectedStream}` : nextBaseName;
};

const isRepeatedPromotionValue = (value, currentClassName = '') => {
  const normalizedValue = normalizeClassLabel(value || '');
  if (!normalizedValue) return false;
  if (normalizedValue === REPEATED_PROMOTION_VALUE) return true;
  return Boolean(currentClassName && normalizedValue === normalizeClassLabel(currentClassName));
};

const normalizePromotedToValue = (value, currentClassName = '') => {
  if (value === undefined || value === null || value === '') return '';
  return isRepeatedPromotionValue(value, currentClassName) ? REPEATED_PROMOTION_VALUE : String(value);
};

const renderDropdownValue = (selected, options, placeholder) =>
  options.find((option) => option.value === selected)?.label || placeholder;

const getOptionsWithCurrentValue = (options, value) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [{ label: value, value }, ...options];
};

const extractArray = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

const makeAbsoluteAssetUrl = (value) => {
  if (!value) return null;
  if (String(value).startsWith('http') || String(value).startsWith('data:')) return value;
  const backendURL = api.defaults.baseURL.replace(/\/api$/, '');
  return `${backendURL}${String(value).startsWith('/') ? '' : '/'}${value}`;
};

const getStudentAttendance = (attendanceData, report) => {
  const studentInfo = report.studentInfo || {};
  const match = attendanceData.find((item) => {
    const itemUserId = String(item.user?._id || item.user || '');
    const itemStudentId = String(item.studentId || item.student?._id || item.student || '');
    return (
      itemUserId === String(studentInfo.id || studentInfo.studentUserId || '') ||
      itemStudentId === String(studentInfo.studentId || studentInfo.studentDocId || '') ||
      item.name === studentInfo.name
    );
  });

  if (!match) return studentInfo.attendance || 0;

  return (match.attendance || []).reduce(
    (total, week) => total + (Number(week.totalPresent) || 0),
    0
  );
};

const calculateTimesOpen = (attendanceData, selectedTerm) => {
  const isDayOpen = (weekNumber, dayKey) =>
    attendanceData.some((student) => {
      const weekRecord = student.attendance?.find((item) => item.weekNumber === weekNumber);
      const value = weekRecord?.days?.[dayKey];
      return value && value !== 'notmarked';
    });

  let maxWeekInAttendance = 0;
  attendanceData.forEach((student) => {
    student.attendance?.forEach((week) => {
      if (week.weekNumber > maxWeekInAttendance) maxWeekInAttendance = week.weekNumber;
    });
  });

  const currentWeekToRender =
    maxWeekInAttendance > 0
      ? maxWeekInAttendance
      : typeof selectedTerm?.weeks === 'number'
        ? selectedTerm.weeks
        : 12;

  let totalOpen = 0;
  const dayKeys = ['M', 'T', 'W', 'TH', 'F'];
  for (let week = 1; week <= currentWeekToRender; week += 1) {
    for (const dayKey of dayKeys) {
      if (isDayOpen(week, dayKey)) totalOpen += 1;
    }
  }
  return totalOpen;
};

export default function TeacherExamsReportScreen() {
  const theme = useTheme();
  const teacher = getStoredTeacher();
  const schoolId = getSchoolId(teacher);
  const classes = useMemo(
    () => (Array.isArray(teacher.assignedClasses) ? teacher.assignedClasses : []),
    [teacher.assignedClasses]
  );
  const [terms, setTerms] = useState([]);
  const [globalSubjects, setGlobalSubjects] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(classes[0]?._id || '');
  const [selectedTermId, setSelectedTermId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingReports, setGeneratingReports] = useState(false);
  const [error, setError] = useState('');

  const selectedClass = useMemo(
    () => classes.find((cls) => cls._id === selectedClassId),
    [classes, selectedClassId]
  );
  const selectedClassName = selectedClass ? getClassDisplayName(selectedClass) : '';

  const selectedTerm = useMemo(
    () => terms.find((term) => String(term._id) === String(selectedTermId)),
    [selectedTermId, terms]
  );

  const selectedNextTermBegins = selectedTerm?.nextTermBegins || selectedTerm?.vacationDate || 'TBD';
  const reportNextTermBegins =
    records.find((record) => record.nextTermBegins)?.nextTermBegins || selectedNextTermBegins;
  const nextTermBeginsInputValue = parseReportDateForInput(reportNextTermBegins);

  const currentTeacherUserId = useMemo(
    () =>
      getEntityId(teacher?.teacherUserId) ||
      getEntityId(teacher?.userId) ||
      getEntityId(teacher?.user) ||
      getEntityId(teacher?.id) ||
      getEntityId(teacher?._id),
    [teacher]
  );

  const canEditReportComments = useMemo(() => {
    const classTeacherId = getEntityId(selectedClass?.classTeacher);
    const coClassTeacherId = getEntityId(selectedClass?.coClassTeacher);
    return Boolean(
      currentTeacherUserId &&
        (classTeacherId === currentTeacherUserId || coClassTeacherId === currentTeacherUserId)
    );
  }, [currentTeacherUserId, selectedClass]);

  const promotedToOptions = useMemo(() => {
    if (!canEditReportComments || !selectedClass) return [];
    const nextClassName = getNextClassDisplayName(selectedClass, classes);
    const currentClassName = getClassDisplayName(selectedClass);
    const options = [];
    if (nextClassName) {
      options.push({
        label: `Promote to ${nextClassName}`,
        value: nextClassName,
      });
    }
    if (currentClassName) {
      options.push({
        label: `Repeat in ${currentClassName}`,
        value: REPEATED_PROMOTION_VALUE,
      });
    }
    return options;
  }, [canEditReportComments, classes, selectedClass]);

  const subjectOptions = useMemo(() => {
    const teacherSubjects = normalizeSubjects(teacher, selectedClass);
    return teacherSubjects.length > 0 ? teacherSubjects : globalSubjects;
  }, [globalSubjects, selectedClass, teacher]);

  const selectedSubject = useMemo(
    () => subjectOptions.find((subject) => String(subject._id) === String(selectedSubjectId)),
    [selectedSubjectId, subjectOptions]
  );

  const loadBaseData = useCallback(async () => {
    if (!schoolId) return;
    setError('');
    try {
      const [termsRes, subjectsRes] = await Promise.allSettled([
        api.get('/term/all', { params: { school: schoolId } }),
        api.get('/subjects'),
      ]);

      if (termsRes.status === 'fulfilled') {
        const nextTerms = termsRes.value.data?.allTerms || termsRes.value.data?.terms || [];
        setTerms(nextTerms);
        setSelectedTermId((prev) => prev || nextTerms[0]?._id || '');
      }

      if (subjectsRes.status === 'fulfilled') {
        const nextSubjects = Array.isArray(subjectsRes.value.data)
          ? subjectsRes.value.data.map(subjectFromValue).filter(Boolean)
          : [];
        setGlobalSubjects(nextSubjects);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load setup data.'));
    }
  }, [schoolId]);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    setSelectedSubjectId((prev) => {
      if (subjectOptions.some((subject) => String(subject._id) === String(prev))) return prev;
      return subjectOptions[0]?._id || '';
    });
  }, [subjectOptions]);

  const loadMarks = useCallback(async () => {
    if (!schoolId || !selectedClassId || !selectedTermId || !selectedSubjectId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/sba-v2/marks', {
        params: {
          schoolId,
          classId: selectedClassId,
          termId: selectedTermId,
          subjectId: selectedSubjectId,
          _: Date.now(),
        },
      });
      const nextRecords = Array.isArray(res.data?.data) ? res.data.data : [];
      setRecords(
        nextRecords.map((record) => ({
          ...record,
          promotedTo: normalizePromotedToValue(record.promotedTo, selectedClassName),
        }))
      );
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load marks.'));
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedClassId, selectedTermId, selectedSubjectId, selectedClassName]);

  useEffect(() => {
    loadMarks();
  }, [loadMarks]);

  const updateRecord = (studentKey, field, value) => {
    setRecords((prev) =>
      prev.map((record) =>
        (record.studentDocId || record.student) === studentKey
          ? { ...record, [field]: value }
          : record
      )
    );
  };

  const saveRecordsToServer = async (recordsToSave = records) => {
    if (!schoolId || !selectedClassId || !selectedTermId || !selectedSubjectId) return;
    await api.post('/sba-v2/marks', {
      schoolId,
      classId: selectedClassId,
      termId: selectedTermId,
      subjectId: selectedSubjectId,
      records: recordsToSave,
    });
  };

  const handleSave = async () => {
    if (!schoolId || !selectedClassId || !selectedTermId || !selectedSubjectId) return;
    setSaving(true);
    setError('');
    try {
      await saveRecordsToServer(records);
      toast.success('Marks saved.');
      loadMarks();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save marks.'));
    } finally {
      setSaving(false);
    }
  };

  const handleNextTermBeginsChange = async (event) => {
    const formattedDate = formatDateForReport(event.target.value);
    const updatedRecords = records.map((record) => ({
      ...record,
      nextTermBegins: formattedDate,
    }));
    setRecords(updatedRecords);

    if (!formattedDate || updatedRecords.length === 0) return;

    setSaving(true);
    setError('');
    try {
      await saveRecordsToServer(updatedRecords);
      toast.success('Next term begins date saved.');
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to save next term begins date.');
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewReportCards = async () => {
    if (!schoolId || !selectedClassId || !selectedTermId) {
      toast.error('Select a class and term before previewing report cards.');
      return;
    }

    setGeneratingReports(true);
    setError('');

    try {
      const reportsRes = await api.get('/sba-v2/reports', {
        params: {
          schoolId,
          classId: selectedClassId,
          termId: selectedTermId,
          _: Date.now(),
        },
      });

      const reportCards = extractArray(reportsRes.data, ['data', 'reportCards', 'reports']);
      if (reportCards.length === 0) {
        toast.warning('No report cards found for the selected class and term.');
        return;
      }

      const className = selectedClass ? getClassDisplayName(selectedClass) : 'Class';
      const termName = selectedTerm?.name || selectedTerm?.term || 'Term';
      const academicYear = selectedTerm?.academicYear || new Date().getFullYear();

      const [schoolInfoResult, attendanceResult] = await Promise.allSettled([
        api.get('/school-info', { params: { schoolId } }),
        api.get('/attendance/class/term', {
          params: {
            classId: selectedClassId,
            termId: selectedTermId,
          },
        }),
      ]);

      const schoolInfoDetails =
        schoolInfoResult.status === 'fulfilled'
          ? schoolInfoResult.value.data?.data || schoolInfoResult.value.data
          : null;
      const attendanceData =
        attendanceResult.status === 'fulfilled'
          ? extractArray(attendanceResult.value.data, ['data', 'attendance', 'records'])
          : [];
      const timesOpen = calculateTimesOpen(attendanceData, selectedTerm);

      const logo =
        schoolInfoDetails?.school?.logo ||
        schoolInfoDetails?.logo ||
        teacher?.school?.logo;
      const adminSignature =
        schoolInfoDetails?.headTeacherSignature ||
        schoolInfoDetails?.headteacherSignature ||
        schoolInfoDetails?.adminSignature;

      const schoolInfo = {
        name: schoolInfoDetails?.school?.name || teacher?.school?.name || 'School Name',
        address: schoolInfoDetails?.address || teacher?.school?.address || 'School Address',
        email: schoolInfoDetails?.email || teacher?.school?.email || 'email@school.com',
        phone: schoolInfoDetails?.phone || teacher?.school?.phone || '',
        motto: schoolInfoDetails?.motto || teacher?.school?.motto || '',
        logo: makeAbsoluteAssetUrl(logo),
        adminName:
          schoolInfoDetails?.headTeacherName ||
          schoolInfoDetails?.adminName ||
          teacher?.school?.adminName ||
          'Admin',
        adminSignature: makeAbsoluteAssetUrl(adminSignature),
      };

      const processedReportCards = reportCards.map((report) => ({
        ...report,
        studentInfo: {
          ...(report.studentInfo || {}),
          profilePicture: makeAbsoluteAssetUrl(report.studentInfo?.profilePicture),
          attendance: getStudentAttendance(attendanceData, report),
          timesOpen,
          nextTermBegins: reportNextTermBegins || report.studentInfo?.nextTermBegins,
        },
      }));

      const teacherNextTermBegins = processedReportCards.find(
        (report) => report.studentInfo?.nextTermBegins
      )?.studentInfo?.nextTermBegins;
      const nextTermBegins =
        teacherNextTermBegins ||
        reportNextTermBegins ||
        'TBD';
      const vacationDate = selectedTerm?.endDate ? new Date(selectedTerm.endDate) : null;
      const reportDate = vacationDate && !Number.isNaN(vacationDate.getTime())
        ? vacationDate.toLocaleDateString('en-GB', { timeZone: 'UTC' })
        : 'TBD';

      const doc = (
        <ClassReportCardsDocument
          reportCards={processedReportCards}
          schoolInfo={schoolInfo}
          className={className}
          termName={termName}
          academicYear={academicYear}
          noOnRoll={reportCards.length}
          nextTermBegins={nextTermBegins}
          reportDate={reportDate}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');

      if (!openedWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = `ReportCards_${className}_${termName}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Report card preview opened.');
    } catch (err) {
      console.error('Failed to preview report cards', err);
      const message = err.response?.data?.message || err.message || 'Failed to preview report cards.';
      setError(message);
      toast.error(message);
    } finally {
      setGeneratingReports(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Exams and Report</Typography>
          <Typography color="text.secondary">Enter SBA, exams, and report comments.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshOutlined />} onClick={loadMarks} disabled={loading} variant="outlined" sx={{ textTransform: 'none' }}>Refresh</Button>
          <Button
            startIcon={generatingReports ? <CircularProgress size={18} /> : <PictureAsPdfOutlined />}
            onClick={handlePreviewReportCards}
            disabled={generatingReports || !selectedClassId || !selectedTermId}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            {generatingReports ? 'Opening...' : 'Preview Report Cards'}
          </Button>
          <Button startIcon={<SaveOutlined />} onClick={handleSave} disabled={saving || records.length === 0} variant="contained" sx={{ textTransform: 'none' }}>Save</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Card sx={teacherPortalTableCardSx(theme)}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 34, md: 42 }, height: { xs: 34, md: 42 } }}>
              <FilterListOutlined fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Report Setup & Filters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select the class, term, and subject before entering marks.
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select label="Class" value={selectedClassId} onChange={(event) => setSelectedClassId(event.target.value)}>
                  {classes.map((cls) => <MenuItem key={cls._id} value={cls._id}>{getClassDisplayName(cls)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Term</InputLabel>
                <Select label="Term" value={selectedTermId} onChange={(event) => setSelectedTermId(event.target.value)}>
                  {terms.map((term) => <MenuItem key={term._id} value={term._id}>{term.term} • {term.academicYear}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select label="Subject" value={selectedSubjectId} onChange={(event) => setSelectedSubjectId(event.target.value)}>
                  {subjectOptions.map((subject) => <MenuItem key={subject._id} value={subject._id}>{subject.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {[
          { label: 'Students Loaded', value: records.length, icon: GroupsOutlined, color: 'primary.main' },
          { label: 'Selected Class', value: selectedClass ? getClassDisplayName(selectedClass) : 'Class', icon: SchoolOutlined, color: 'info.main' },
          { label: 'Subject', value: selectedSubject?.name || 'Subject', icon: SubjectOutlined, color: 'success.main' },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <Grid item xs={12} md={4} key={stat.label} sx={teacherPortalGridItemSx}>
              <Card sx={teacherPortalStatCardSx(theme)}>
                <CardContent sx={{ width: '100%' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: stat.color }}>
                      <Icon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Typography variant="h6" fontWeight={900} noWrap>
                        {stat.value}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {records.length > 0 && canEditReportComments && (
        <Card sx={teacherPortalTableCardSx(theme)}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(59,130,246,0.18)' : '#EAF2FF',
                    color: 'primary.main',
                    width: { xs: 38, md: 44 },
                    height: { xs: 38, md: 44 },
                  }}
                >
                  <CalendarMonthOutlined fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="overline" fontWeight={900} color="text.secondary">
                    NEXT TERM BEGINS
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {reportNextTermBegins || 'Select date'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This reopening date will appear on every report card for the selected class.
                  </Typography>
                </Box>
              </Stack>
              <TextField
                label="Select date"
                type="date"
                value={nextTermBeginsInputValue}
                onChange={handleNextTermBeginsChange}
                disabled={saving}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', md: 220 } }}
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card sx={teacherPortalTableCardSx(theme)}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'success.main', width: { xs: 34, md: 40 }, height: { xs: 34, md: 40 } }}>
                <SaveOutlined fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  Marks Entry ({records.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedSubject?.name || 'No subject selected'}
                  {canEditReportComments
                    ? ''
                    : ' • Report comments are only editable by the class teacher or co-class teacher.'}
                </Typography>
              </Box>
            </Stack>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900, minWidth: 180 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>CW/10</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>T1/20</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>T2/20</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Proj/10</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Exam/100</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Conduct</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Interest</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Remarks</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Promoted To</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((record) => {
                    const key = record.studentDocId || record.student;
                    return (
                      <TableRow key={key}>
                        <TableCell sx={{ fontWeight: 700 }}>{record.studentName}</TableCell>
                        {['classWork', 'classTest1', 'classTest2', 'projectWork', 'exams'].map((field) => (
                          <TableCell key={field}>
                            <TextField
                              type="number"
                              value={record[field] ?? ''}
                              onChange={(event) => updateRecord(key, field, event.target.value)}
                              size="small"
                              sx={{ width: 86 }}
                            />
                          </TableCell>
                        ))}
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 230 }} disabled={!canEditReportComments}>
                            <Select
                              displayEmpty
                              value={record.conduct ?? ''}
                              onChange={(event) => updateRecord(key, 'conduct', event.target.value)}
                              renderValue={(selected) =>
                                renderDropdownValue(selected, CONDUCT_OPTIONS, 'Select conduct')
                              }
                            >
                              <MenuItem value="">Select conduct</MenuItem>
                              {getOptionsWithCurrentValue(CONDUCT_OPTIONS, record.conduct).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 180 }} disabled={!canEditReportComments}>
                            <Select
                              displayEmpty
                              value={record.interest ?? ''}
                              onChange={(event) => updateRecord(key, 'interest', event.target.value)}
                              renderValue={(selected) =>
                                renderDropdownValue(selected, INTEREST_OPTIONS, 'Select interest')
                              }
                            >
                              <MenuItem value="">Select interest</MenuItem>
                              {getOptionsWithCurrentValue(INTEREST_OPTIONS, record.interest).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 250 }} disabled={!canEditReportComments}>
                            <Select
                              displayEmpty
                              value={record.teacherRemarks ?? ''}
                              onChange={(event) => updateRecord(key, 'teacherRemarks', event.target.value)}
                              renderValue={(selected) =>
                                renderDropdownValue(selected, REMARKS_OPTIONS, 'Select remarks')
                              }
                            >
                              <MenuItem value="">Select remarks</MenuItem>
                              {getOptionsWithCurrentValue(REMARKS_OPTIONS, record.teacherRemarks).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <FormControl
                            size="small"
                            sx={{ minWidth: 210 }}
                            disabled={!canEditReportComments || promotedToOptions.length === 0}
                          >
                            <Select
                              displayEmpty
                              value={record.promotedTo ?? ''}
                              onChange={(event) => updateRecord(key, 'promotedTo', event.target.value)}
                              renderValue={(selected) =>
                                renderDropdownValue(
                                  selected,
                                  getOptionsWithCurrentValue(promotedToOptions, record.promotedTo),
                                  promotedToOptions.length > 0 ? 'Select class' : 'No next class found'
                                )
                              }
                            >
                              <MenuItem value="">
                                {promotedToOptions.length > 0 ? 'Select class' : 'No next class found'}
                              </MenuItem>
                              {getOptionsWithCurrentValue(promotedToOptions, record.promotedTo).map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {records.length === 0 && (
                <Typography color="text.secondary" sx={{ p: 3 }}>
                  No students found for this class and subject.
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
